<template>
  <Panel>
    <template #footer>
      <div class="flex">
        <Button
          v-if="image.versions.length > 1"
          severity="secondary"
          variant="text"
          size="small"
          @click="toggleVersion"
        >
          {{ selectedVersionIndex === 0 ? 'Show edited' : 'Show original' }}
        </Button>
        <div
          v-if="image.attentionText"
          class="flex-1 text-yellow-400 ml-auto attention-text"
          style="word-break: break-word; white-space: pre-line"
        >
          {{ image.attentionText }}
        </div>
        <i
          v-if="image.attention"
          class="pi pi-exclamation-triangle text-yellow-400 !text-3xl ml-auto"
        ></i>
      </div>
    </template>
    <a :href="imageFullSizeUrl" target="_blank">
      <img :src="imageThumbnailUrl" class="product-image" />
    </a>
  </Panel>
</template>

<style scoped>
.product-image {
  max-width: 400px;
  height: auto;
  display: block;
}

.attention-text {
  max-width: 300px;
}
</style>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import type { ImageDto } from '@/api/generated';
import Panel from 'primevue/panel';
import Button from 'primevue/button';

const props = defineProps<{
  productId: string;
  image: ImageDto;
}>();

const selectedVersionIndex = ref(0);

function toggleVersion() {
  if (selectedVersionIndex.value === 0) {
    selectVersionSelectedOnImage();
  } else {
    selectedVersionIndex.value = 0;
  }
}

const _selectedImages = defineModel('selectedImages');

const imageThumbnailUrl = computed(() => {
  return getImageUrl(props.image, {
    width: 400,
    selectedVersionIndex: selectedVersionIndex.value,
  });
});

const imageFullSizeUrl = computed(() => {
  return getImageUrl(props.image, {
    fullSize: true,
    selectedVersionIndex: selectedVersionIndex.value,
  });
});

function getImageUrl(
  image: ImageDto,
  opts: {
    fullSize?: boolean;
    width?: number;
    selectedVersionIndex?: number;
  },
): string | undefined {
  let imageOpts = '';
  if (opts.fullSize) {
    imageOpts += 's0';
  } else if (opts.width) {
    const width = devicePixelRatio * opts.width;
    imageOpts += `s${width}`;
  }

  const selectedVersion = image.versions[opts.selectedVersionIndex ?? 0];
  const url = selectedVersion?.url;

  if (!url) {
    return undefined;
  }

  return imageOpts.length ? `${url}=${imageOpts}` : (url ?? undefined);
}

function selectVersionSelectedOnImage() {
  selectedVersionIndex.value = props.image.versions.findIndex(
    v => v.id === props.image.selectedVersionId,
  );
  if (selectedVersionIndex.value === -1 && props.image.versions.length > 0) {
    selectedVersionIndex.value = props.image.versions.length - 1;
  }
}

onMounted(() => {
  selectVersionSelectedOnImage();
});
</script>
